const DEFAULT_TZ = 'Australia/Sydney';

const dtfCache = new Map();

const getDateTimeFormatter = (timeZone) => {
  if (!timeZone) timeZone = DEFAULT_TZ;
  if (!dtfCache.has(timeZone)) {
    dtfCache.set(
      timeZone,
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    );
  }
  return dtfCache.get(timeZone);
};

const getTimeZoneOffsetMs = (date, timeZone = DEFAULT_TZ) => {
  const dtf = getDateTimeFormatter(timeZone);
  const parts = dtf.formatToParts(date);
  const values = {};
  parts.forEach(({ type, value }) => {
    if (type !== 'literal') {
      values[type] = value;
    }
  });

  const year = Number(values.year);
  const month = Number(values.month);
  const day = Number(values.day);
  const hour = Number(values.hour);
  const minute = Number(values.minute);
  const second = Number(values.second);

  return Date.UTC(year, month - 1, day, hour, minute, second) - date.getTime();
};

const pad = (value) => value.toString().padStart(2, '0');

export const zonedTimeToUtc = (dateTimeString, timeZone = DEFAULT_TZ) => {
  if (!dateTimeString) return null;

  const [datePart, timePart = '00:00'] = dateTimeString.split('T');
  if (!datePart) return null;

  const [yearStr, monthStr, dayStr] = datePart.split('-');
  const [hourStr = '00', minuteStr = '00'] = timePart.split(':');

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (
    [year, month, day, hour, minute].some(
      (num) => Number.isNaN(num) || !Number.isFinite(num)
    )
  ) {
    return null;
  }

  // The dateTimeString is in the specified timezone (e.g., "2025-10-29T00:00" in Australia/Sydney)
  // We need to create a date that represents this local time and then get its UTC equivalent
  
  // Create a date string with timezone info to force interpretation in that timezone
  // For Australia/Sydney, we need to account for DST
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
  
  // Parse as a local date first to establish the date
  const localDate = new Date(dateStr + '+00:00'); // This treats it as UTC
  if (Number.isNaN(localDate.getTime())) return null;
  
  // Now we need to get the offset for this date in the target timezone
  const offset = getTimeZoneOffsetMs(localDate, timeZone);
  
  // Convert: local time in timezone -> UTC
  // If offset is positive, that means the timezone is ahead of UTC
  // So to convert from timezone to UTC, we subtract the offset
  return new Date(localDate.getTime() - offset);
};

export const utcToZonedTimeString = (isoString, timeZone = DEFAULT_TZ) => {
  if (!isoString) return '';
  
  const trimmed = typeof isoString === 'string' ? isoString.trim() : isoString;
  
  // If the input has timezone info (Z or offset), parse it as UTC and convert to timezone
  const hasTz = /([+-]\d{2}:\d{2}|Z)$/i.test(trimmed);
  
  if (hasTz) {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const dtf = getDateTimeFormatter(timeZone);
    const parts = dtf.formatToParts(date);
    const values = {};
    parts.forEach(({ type, value }) => {
      if (type !== 'literal') {
        values[type] = value;
      }
    });
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  }
  
  // MySQL DATETIME format: "2026-04-07 23:59:00" or "2026-04-07 23:59"
  // Convert to ISO format "2026-04-07T23:59" without any timezone conversion
  if (trimmed.includes(' ')) {
    const parts = trimmed.split(' ');
    if (parts.length === 2) {
      const datePart = parts[0];
      const timePart = parts[1].split(':').slice(0, 2).join(':');
      return `${datePart}T${timePart}`;
    }
  }
  
  // If it's already in ISO format without timezone (YYYY-MM-DDTHH:mm), return as-is
  const isoNoTz = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)$/;
  if (isoNoTz.test(trimmed)) {
    const parts = trimmed.split('T');
    const timePart = parts[1].split(':').slice(0, 2).join(':');
    return `${parts[0]}T${timePart}`;
  }
  
  // Don't try to parse with Date constructor - just return what we have
  return '';
};

export const formatZonedDisplay = (dateTimeString) => {
  if (!dateTimeString) return '';
  const [datePart, timePart = ''] = dateTimeString.split('T');
  if (!datePart || !timePart) return '';

  const [year, month, day] = datePart.split('-');
  const [hour, minute] = timePart.split(':');

  if (!year || !month || !day || !hour || !minute) return '';

  return `${day}/${month}/${year} ${hour}:${minute}`;
};

export const DEFAULT_TIME_ZONE = DEFAULT_TZ;
